import { useState, useMemo, useCallback } from 'react';
import styles from './DynamicFormBuilder.module.css';

// All help content is supplied by the server (see DynamicForms::Field —
// config_option / summary / example / Field.reference, surfaced through the
// typeCatalog + fieldReference props). This component only renders it, so the
// docs stay co-located with the validation that defines each config key.

function CopyButton({ text, label = 'Copy' }) {
  const copy = useCallback(() => {
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
  }, [text]);
  return (
    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copy}>
      {label}
    </button>
  );
}

function OptionRow({ name, kind, values, description }) {
  const valueList = Array.isArray(values) && values.length
    ? values.map((v) => JSON.stringify(v)).join(' | ')
    : null;
  return (
    <li className="mb-1">
      <code>{name}</code>
      {kind && <span className="text-muted"> · {kind}</span>}
      {valueList && <span className="text-muted"> · {valueList}</span>}
      {description ? <div className="small">{description}</div>
        : <div className="small fst-italic text-muted">Undocumented — see field class.</div>}
    </li>
  );
}

function TypeEntry({ entry }) {
  const options = Array.isArray(entry.config_options) ? entry.config_options : [];
  return (
    <details className="border rounded p-2">
      <summary className="fw-semibold">
        <code>{entry.type}</code> <span className="text-muted fw-normal">— {entry.label}</span>
      </summary>
      {entry.summary && <p className="small mt-2 mb-1">{entry.summary}</p>}
      {options.length > 0 ? (
        <>
          <div className="text-uppercase text-muted small mt-2">Config keys</div>
          <ul className="list-unstyled mb-2 ps-0">
            {options.map((o) => (
              <OptionRow key={o.key} name={o.key} kind={o.kind} values={o.values} description={o.description} />
            ))}
          </ul>
        </>
      ) : (
        <p className="small fst-italic text-muted mt-2 mb-2">No type-specific config.</p>
      )}
      {entry.example && (
        <div className="d-flex flex-column gap-1">
          <div className="d-flex justify-content-between align-items-baseline">
            <span className="text-uppercase text-muted small">Example</span>
            <CopyButton text={entry.example} />
          </div>
          <pre className="bg-light border rounded p-2 small mb-0 font-monospace">{entry.example}</pre>
        </div>
      )}
    </details>
  );
}

export default function JsonHelpPanel({ typeCatalog, fieldReference }) {
  const [filter, setFilter] = useState('');
  const catalog = Array.isArray(typeCatalog) ? typeCatalog : [];
  const ref = fieldReference || {};
  const baseFields = Array.isArray(ref.base_fields) ? ref.base_fields : [];
  const commonConfig = Array.isArray(ref.common_config) ? ref.common_config : [];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((e) =>
      e.type.toLowerCase().includes(q) || (e.label || '').toLowerCase().includes(q));
  }, [catalog, filter]);

  return (
    <aside className={`d-flex flex-column gap-2 ${styles.helpPanel}`} aria-label="JSON field help">
      <h4 className="m-0 text-uppercase text-muted small">Field reference</h4>

      <details className="border rounded p-2" open>
        <summary className="fw-semibold">Field anatomy</summary>
        {baseFields.length > 0 && (
          <>
            <div className="text-uppercase text-muted small mt-2">Base attributes (all fields)</div>
            <ul className="list-unstyled mb-2 ps-0">
              {baseFields.map((f) => (
                <OptionRow key={f.name} name={f.name} kind={f.kind} description={f.description} />
              ))}
            </ul>
          </>
        )}
        {ref.options_doc && (
          <>
            <div className="text-uppercase text-muted small">Options shape</div>
            <p className="small mb-2">{ref.options_doc}</p>
          </>
        )}
        {ref.visibility_doc && (
          <>
            <div className="text-uppercase text-muted small">Visibility shape</div>
            <p className="small mb-2">{ref.visibility_doc}</p>
          </>
        )}
        {commonConfig.length > 0 && (
          <>
            <div className="text-uppercase text-muted small">Common config (all types)</div>
            <ul className="list-unstyled mb-0 ps-0">
              {commonConfig.map((c) => (
                <OptionRow key={c.key} name={c.key} kind={c.kind} description={c.description} />
              ))}
            </ul>
          </>
        )}
      </details>

      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Filter field types…"
        aria-label="Filter field types"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      {filtered.map((entry) => (
        <TypeEntry key={entry.type} entry={entry} />
      ))}
      {filtered.length === 0 && (
        <p className="small fst-italic text-muted mb-0">No field types match.</p>
      )}
    </aside>
  );
}
